import React, { useState, useEffect, useRef } from "react";
import { Form, Table, Container, Row, Col, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchExpenses } from "../../slice/ExpenseSlice";
import { fetchCategories } from "../../slice/ExpenseCategorySlice";
import { handleExportPDF, handleExportExcel } from "../../pdf/ExpenseReportPDF";

const ExpenseReport = () => {
  const dispatch = useDispatch();
  const expenses = useSelector((state) => state.expense.expenses);
  const categories = useSelector((state) => state.categories.categories);
  const printRef = useRef();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [filteredExpenses, setFilteredExpenses] = useState([]);

  useEffect(() => {
    dispatch(fetchExpenses());
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    let filtered = expenses.flatMap((expense) =>
      expense.expense_data.map((data) => ({
        date: expense.expense_date,
        category_name: data.category_name,
        description: data.description,
        amount: data.amount,
      })),
    );

    if (fromDate) {
      filtered = filtered.filter((exp) => exp.date >= fromDate);
    }
    if (toDate) {
      filtered = filtered.filter((exp) => exp.date <= toDate);
    }
    if (selectedCategory !== "All Categories") {
      filtered = filtered.filter(
        (exp) => exp.category_name === selectedCategory,
      );
    }

    setFilteredExpenses(filtered);
  }, [expenses, fromDate, toDate, selectedCategory]);

  const handleClearFilter = () => {
    setFromDate("");
    setToDate("");
    setSelectedCategory("All Categories");
  };

  // Group expenses by date
  const groupedExpenses = filteredExpenses.reduce((acc, exp) => {
    if (!acc[exp.date]) {
      acc[exp.date] = [];
    }
    acc[exp.date].push(exp);
    return acc;
  }, {});

  const headers = ["Date", "Category", "Description", "Income", "Expense"];

  // Calculate row spans for each date
  const dateRowSpans = {};
  Object.keys(groupedExpenses).forEach((date) => {
    dateRowSpans[date] = groupedExpenses[date].length;
  });

  // Flatten the grouped expenses for rendering
  const rows = Object.keys(groupedExpenses).flatMap((date) =>
    groupedExpenses[date].map((exp, idx) => ({
      ...exp,
      isFirstInGroup: idx === 0,
      rowSpan: dateRowSpans[date],
    })),
  );

  return (
    <Container className="py-4">
      <Row className="mb-4 align-items-end">
        <Col xs={12} md={2} className="mb-3 mb-md-0">
          <Form.Group>
            <Form.Label>From Date</Form.Label>
            <Form.Control
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={2} className="mb-3 mb-md-0">
          <Form.Group>
            <Form.Label>To Date</Form.Label>
            <Form.Control
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={2} className="mb-3 mb-md-0">
          <Form.Group>
            <Form.Label>Category</Form.Label>
            <Form.Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option>All Categories</option>
              {categories.map((category) => (
                <option
                  key={category.category_id}
                  value={category.category_name}
                >
                  {category.category_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12} md={6} className="d-flex gap-2 align-items-end">
          <Button variant="secondary" onClick={handleClearFilter}>
            Clear Filter
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              handleExportPDF(
                filteredExpenses,
                fromDate,
                toDate,
                selectedCategory,
              )
            }
          >
            View PDF
          </Button>
          <Button
            variant="success"
            onClick={() => handleExportExcel(filteredExpenses)}
          >
            Excel
          </Button>
        </Col>
      </Row>

      <div className="table-responsive" ref={printRef}>
        <Table bordered>
          <thead>
            <tr>
              {headers.map((head, idx) => (
                <th key={idx} className="text-center">
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center">
                  No data found.
                </td>
              </tr>
            ) : (
              rows.map((exp, idx) => (
                <tr key={`${exp.date}-${idx}`}>
                  {exp.isFirstInGroup ? (
                    <td className="text-center" rowSpan={exp.rowSpan}>
                      {exp.date}
                    </td>
                  ) : null}
                  <td className="text-center">{exp.category_name}</td>
                  <td className="text-center">{exp.description || "-"}</td>
                  <td className="text-center">0</td>
                  <td className="text-center">{exp.amount}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </Container>
  );
};

export default ExpenseReport;
